import { useEffect, useState } from 'react';
import { Listbox } from '@headlessui/react';
import moment from 'moment';
import {
	PaperClipIcon,
	CheckIcon,
	ChevronUpDownIcon,
} from '@heroicons/react/20/solid';
import {
	PencilIcon,
	PlusIcon,
	FolderIcon,
	UserGroupIcon,
	ClipboardDocumentListIcon,
	DocumentTextIcon,
	ChatBubbleLeftIcon,
	ClockIcon,
} from '@heroicons/react/24/outline';

import {
	getProject,
	updateProject,
	updateProjectStatus,
	createProjectMilestone,
	updateProjectMilestone,
	deleteProjectMilestone,
	addProjectComment,
} from '../api/projects';
import { getTeam } from '../api/company';

import {
	StackableModal,
	Tabs,
	StatusBadge,
	Badge,
} from '../components/nextgen';
import { classNames, getUserFullName } from '../components/helpers/functions';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';
import { AvatarGenerated } from '../components/Avatar';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

const MODAL_NAME = 'projectOverview';

// Status configuration
const STATUS_OPTIONS = [
	{ key: 'planning', name: 'Planning', color: 'blue' },
	{ key: 'active', name: 'Active', color: 'green' },
	{ key: 'on_hold', name: 'On Hold', color: 'yellow' },
	{ key: 'completed', name: 'Completed', color: 'gray' },
	{ key: 'archived', name: 'Archived', color: 'gray' },
];

const MILESTONE_STATUS_OPTIONS = [
	{ key: 'pending', name: 'Pending', color: 'gray' },
	{ key: 'in_progress', name: 'In Progress', color: 'blue' },
	{ key: 'completed', name: 'Completed', color: 'green' },
	{ key: 'cancelled', name: 'Cancelled', color: 'red' },
];

// Progress bar component
const ProgressBar = ({ progress }) => (
	<div className="flex items-center gap-3">
		<div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
			<div
				className="h-full bg-blue-600 rounded-full transition-all duration-300"
				style={{ width: `${progress || 0}%` }}
			/>
		</div>
		<span className="text-sm font-medium text-gray-700">{progress || 0}%</span>
	</div>
);

// Overview Tab
const OverviewTab = ({ project, onUpdate }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editForm, setEditForm] = useState({});

	const { value: team } = useAsync(getTeam);

	const handleEdit = () => {
		setEditForm({
			name: project.name,
			description: project.description || '',
			startDate: project.startDate || '',
			targetEndDate: project.targetEndDate || '',
			managerId: project.managerId,
		});
		setIsEditing(true);
	};

	const handleSave = async () => {
		await updateProject({ id: project.id, ...editForm });
		onUpdate();
		setIsEditing(false);
	};

	const selectedManager = team?.rows?.find((u) => u.id === editForm.managerId);

	return (
		<div className="space-y-6">
			{/* Progress */}
			<div className="bg-gray-50 rounded-lg p-4">
				<h4 className="text-sm font-medium text-gray-700 mb-2">Progress</h4>
				<ProgressBar progress={project.progress} />
			</div>

			{/* Details */}
			<div className="border rounded-lg divide-y">
				{isEditing ? (
					<div className="p-4 space-y-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Project Name
							</label>
							<input
								type="text"
								value={editForm.name}
								onChange={(e) =>
									setEditForm((prev) => ({ ...prev, name: e.target.value }))
								}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Description
							</label>
							<textarea
								value={editForm.description}
								onChange={(e) =>
									setEditForm((prev) => ({ ...prev, description: e.target.value }))
								}
								rows={4}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg"
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Start Date
								</label>
								<input
									type="date"
									value={editForm.startDate}
									onChange={(e) =>
										setEditForm((prev) => ({ ...prev, startDate: e.target.value }))
									}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Target End Date
								</label>
								<input
									type="date"
									value={editForm.targetEndDate}
									onChange={(e) =>
										setEditForm((prev) => ({ ...prev, targetEndDate: e.target.value }))
									}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg"
								/>
							</div>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Project Manager
							</label>
							<Listbox
								value={editForm.managerId}
								onChange={(v) => setEditForm((prev) => ({ ...prev, managerId: v }))}
							>
								<div className="relative">
									<Listbox.Button className="relative w-full cursor-default rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left">
										<span className="block truncate">
											{selectedManager ? getUserFullName(selectedManager) : 'Select manager'}
										</span>
										<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
											<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
										</span>
									</Listbox.Button>
									<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg border">
										{team?.rows?.map((user) => (
											<Listbox.Option
												key={user.id}
												value={user.id}
												className={({ active }) =>
													classNames(
														'relative cursor-default select-none py-2 pl-10 pr-4',
														active ? 'bg-blue-100' : '',
													)
												}
											>
												{({ selected }) => (
													<>
														<span className={selected ? 'font-medium' : ''}>
															{getUserFullName(user)}
														</span>
														{selected && (
															<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
																<CheckIcon className="h-5 w-5" />
															</span>
														)}
													</>
												)}
											</Listbox.Option>
										))}
									</Listbox.Options>
								</div>
							</Listbox>
						</div>
						<div className="flex justify-end gap-3">
							<button
								onClick={() => setIsEditing(false)}
								className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
							>
								Cancel
							</button>
							<button
								onClick={handleSave}
								className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
							>
								Save
							</button>
						</div>
					</div>
				) : (
					<>
						<div className="p-4 flex justify-between items-start">
							<div>
								<h4 className="text-sm font-medium text-gray-500">Description</h4>
								<p className="mt-1 text-gray-900 whitespace-pre-wrap">
									{project.description || 'No description'}
								</p>
							</div>
							<button
								onClick={handleEdit}
								className="p-2 text-gray-400 hover:text-gray-600"
							>
								<PencilIcon className="w-5 h-5" />
							</button>
						</div>
						<div className="p-4 grid grid-cols-2 gap-4">
							<div>
								<h4 className="text-sm font-medium text-gray-500">Start Date</h4>
								<p className="mt-1 text-gray-900">
									{project.startDate ? moment(project.startDate).format('MMM D, YYYY') : '-'}
								</p>
							</div>
							<div>
								<h4 className="text-sm font-medium text-gray-500">Target End Date</h4>
								<p className="mt-1 text-gray-900">
									{project.targetEndDate
										? moment(project.targetEndDate).format('MMM D, YYYY')
										: '-'}
								</p>
							</div>
						</div>
						<div className="p-4">
							<h4 className="text-sm font-medium text-gray-500">Project Manager</h4>
							<p className="mt-1 text-gray-900">
								{project.manager ? getUserFullName(project.manager) : '-'}
							</p>
						</div>
						<div className="p-4">
							<h4 className="text-sm font-medium text-gray-500">Category</h4>
							<p className="mt-1">
								{project.category ? (
									<Badge color="gray">{project.category.name}</Badge>
								) : (
									'-'
								)}
							</p>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

// Milestones Tab
const MilestonesTab = ({ project, onUpdate }) => {
	const [isAdding, setIsAdding] = useState(false);
	const [newMilestone, setNewMilestone] = useState({ name: '', targetDate: '' });

	const handleAdd = async () => {
		if (!newMilestone.name.trim()) return;
		await createProjectMilestone({ id: project.id, ...newMilestone });
		setNewMilestone({ name: '', targetDate: '' });
		setIsAdding(false);
		onUpdate();
	};

	const handleStatusChange = async (milestoneId, status) => {
		await updateProjectMilestone({ id: project.id, milestoneId, status });
		onUpdate();
	};

	const milestones = project.milestones || [];
	const completedCount = milestones.filter((m) => m.status === 'completed').length;

	return (
		<div className="space-y-4">
			{/* Summary */}
			<div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
				<div>
					<span className="text-2xl font-semibold text-gray-900">
						{completedCount}/{milestones.length}
					</span>
					<span className="text-gray-500 ml-2">milestones completed</span>
				</div>
				<button
					onClick={() => setIsAdding(true)}
					className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
				>
					<PlusIcon className="w-4 h-4" />
					Add Milestone
				</button>
			</div>

			{/* Add form */}
			{isAdding && (
				<div className="border rounded-lg p-4 space-y-4">
					<input
						type="text"
						placeholder="Milestone name"
						value={newMilestone.name}
						onChange={(e) =>
							setNewMilestone((prev) => ({ ...prev, name: e.target.value }))
						}
						className="w-full px-3 py-2 border border-gray-300 rounded-lg"
					/>
					<input
						type="date"
						value={newMilestone.targetDate}
						onChange={(e) =>
							setNewMilestone((prev) => ({ ...prev, targetDate: e.target.value }))
						}
						className="w-full px-3 py-2 border border-gray-300 rounded-lg"
					/>
					<div className="flex justify-end gap-3">
						<button
							onClick={() => setIsAdding(false)}
							className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg"
						>
							Cancel
						</button>
						<button
							onClick={handleAdd}
							className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
						>
							Add
						</button>
					</div>
				</div>
			)}

			{/* Milestones list */}
			<div className="border rounded-lg divide-y">
				{milestones.length === 0 ? (
					<div className="p-8 text-center text-gray-500">
						No milestones yet. Add your first milestone to track progress.
					</div>
				) : (
					milestones.map((milestone) => (
						<div key={milestone.id} className="p-4 flex items-center justify-between">
							<div className="flex items-center gap-3">
								<input
									type="checkbox"
									checked={milestone.status === 'completed'}
									onChange={(e) =>
										handleStatusChange(
											milestone.id,
											e.target.checked ? 'completed' : 'pending',
										)
									}
									className="w-5 h-5 rounded border-gray-300 text-blue-600"
								/>
								<div>
									<p
										className={classNames(
											'font-medium',
											milestone.status === 'completed'
												? 'text-gray-400 line-through'
												: 'text-gray-900',
										)}
									>
										{milestone.name}
									</p>
									{milestone.targetDate && (
										<p className="text-sm text-gray-500">
											Target: {moment(milestone.targetDate).format('MMM D, YYYY')}
										</p>
									)}
								</div>
							</div>
							<StatusBadge
								color={
									MILESTONE_STATUS_OPTIONS.find((s) => s.key === milestone.status)?.color ||
									'gray'
								}
							>
								{MILESTONE_STATUS_OPTIONS.find((s) => s.key === milestone.status)?.name ||
									milestone.status}
							</StatusBadge>
						</div>
					))
				)}
			</div>
		</div>
	);
};

// Activity Tab
const ActivityTab = ({ project }) => {
	const activities = project.activities || [];

	return (
		<div className="space-y-4">
			{activities.length === 0 ? (
				<div className="p-8 text-center text-gray-500">No activity yet.</div>
			) : (
				<div className="border rounded-lg divide-y">
					{activities.map((activity) => (
						<div key={activity.id} className="p-4 flex gap-3">
							<div className="flex-shrink-0">
								{activity.addedByUser ? (
									<AvatarGenerated
										name={getUserFullName(activity.addedByUser)}
										size="sm"
									/>
								) : (
									<div className="w-8 h-8 bg-gray-200 rounded-full" />
								)}
							</div>
							<div className="flex-1">
								<p className="text-sm text-gray-900">
									<span className="font-medium">
										{activity.addedByUser
											? getUserFullName(activity.addedByUser)
											: 'System'}
									</span>{' '}
									{activity.type?.replace(/_/g, ' ').replace('.', ': ')}
								</p>
								<p className="text-xs text-gray-500 mt-1">
									{moment(activity.createdAt).fromNow()}
								</p>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

const ProjectOverviewModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const [project, setProject] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [activeTab, setActiveTab] = useState('overview');

	const loadProject = async () => {
		if (!open?.id) return;
		setIsLoading(true);
		try {
			const data = await getProject({ id: open.id });
			setProject(data);
		} catch (error) {
			console.error('Failed to load project:', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (open?.id) {
			loadProject();
			setActiveTab('overview');
		}
	}, [open?.id]);

	const handleClose = () => {
		open?.onClose?.();
		setClose();
	};

	const handleStatusChange = async (status) => {
		await updateProjectStatus({ id: project.id, status });
		loadProject();
	};

	const currentStatus = STATUS_OPTIONS.find((s) => s.key === project?.status);

	const tabs = [
		{ id: 'overview', name: 'Overview', icon: FolderIcon },
		{ id: 'milestones', name: 'Milestones', icon: ClipboardDocumentListIcon },
		{ id: 'activity', name: 'Activity', icon: ClockIcon },
	];

	return (
		<StackableModal
			isOpen={!!open}
			onClose={handleClose}
			title={
				<div className="flex items-center gap-3">
					<div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
						<FolderIcon className="w-5 h-5 text-blue-600" />
					</div>
					<div>
						<h2 className="text-lg font-semibold text-gray-900">
							{project?.name || 'Project'}
						</h2>
						<p className="text-sm text-gray-500">{project?.projectCode}</p>
					</div>
				</div>
			}
			size="xl"
		>
			{isLoading ? (
				<LoadingSkeleton />
			) : project ? (
				<div className="space-y-6">
					{/* Status */}
					<div className="flex items-center gap-4">
						<span className="text-sm text-gray-500">Status:</span>
						<Listbox value={project.status} onChange={handleStatusChange}>
							<div className="relative">
								<Listbox.Button className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer hover:bg-gray-50">
									<StatusBadge color={currentStatus?.color || 'gray'}>
										{currentStatus?.name || project.status}
									</StatusBadge>
									<ChevronUpDownIcon className="w-4 h-4 text-gray-400" />
								</Listbox.Button>
								<Listbox.Options className="absolute z-10 mt-1 w-40 overflow-auto rounded-lg bg-white py-1 shadow-lg border">
									{STATUS_OPTIONS.map((status) => (
										<Listbox.Option
											key={status.key}
											value={status.key}
											className={({ active }) =>
												classNames(
													'relative cursor-default select-none py-2 px-4',
													active ? 'bg-blue-100' : '',
												)
											}
										>
											<StatusBadge color={status.color}>{status.name}</StatusBadge>
										</Listbox.Option>
									))}
								</Listbox.Options>
							</div>
						</Listbox>
					</div>

					{/* Tabs */}
					<Tabs
						tabs={tabs}
						activeTab={activeTab}
						onChange={setActiveTab}
					/>

					{/* Tab content */}
					<div className="min-h-[300px]">
						{activeTab === 'overview' && (
							<OverviewTab project={project} onUpdate={loadProject} />
						)}
						{activeTab === 'milestones' && (
							<MilestonesTab project={project} onUpdate={loadProject} />
						)}
						{activeTab === 'activity' && <ActivityTab project={project} />}
					</div>
				</div>
			) : (
				<div className="p-8 text-center text-gray-500">Project not found</div>
			)}
		</StackableModal>
	);
};

export default ProjectOverviewModal;
